// What the View filter is currently hiding, and how to say it in English.
//
// Hiding a type sets `hidden: true` on its nodes. A hidden node leaves the DOM but
// still counts in `nodes.length`, which is what made this so hard to see: the empty
// state is suppressed (it tests `nodes.length === 0`), fit-view and Auto Layout have
// nothing to act on, and the minimap is blank. Every symptom points at a broken page
// rather than at a filter the writer set weeks ago and localStorage remembered. (#754)
//
// The counting lives here, away from the page, so it can be tested without a canvas.

export type EntityKind = 'character' | 'location' | 'event';
export type TypeVisibility = Record<EntityKind, boolean>;

/** Singular/plural nouns, lowercase, for use mid-sentence. */
const NOUNS: Record<EntityKind, [string, string]> = {
  character: ['character', 'characters'],
  location: ['location', 'locations'],
  event: ['event', 'events'],
};

/** A node as this module needs it: just its kind and whether the filter hid it. */
export interface CountableNode {
  type?: string;
}

export interface HiddenSummary {
  /** Types switched off, in a stable order. */
  hiddenKinds: EntityKind[];
  /** How many nodes each kind has, hidden or not. */
  counts: Record<EntityKind, number>;
  /** Nodes the filter is currently removing. */
  hiddenCount: number;
  /** Nodes that survive the filter. */
  visibleCount: number;
  /**
   * The world is not empty, but the filter leaves nothing on screen. This is the
   * state that reads as a broken page, so it gets its own message rather than the
   * generic "No World Elements Found".
   */
  allHidden: boolean;
  /** True when the View control should show its filtered state. */
  filtering: boolean;
}

export function summarizeHidden(
  nodes: readonly CountableNode[],
  typeVisibility: TypeVisibility,
): HiddenSummary {
  const counts: Record<EntityKind, number> = { character: 0, location: 0, event: 0 };
  for (const n of nodes) {
    const kind = n.type as EntityKind;
    if (kind in counts) counts[kind] += 1;
  }

  // Ordered by the constant, not by Object.keys on the visibility record, so the
  // wording is stable no matter how that object was built.
  const kinds: EntityKind[] = ['character', 'location', 'event'];
  const hiddenKinds = kinds.filter(k => !typeVisibility[k]);

  const hiddenCount = hiddenKinds.reduce((sum, k) => sum + counts[k], 0);
  const visibleCount = nodes.length - hiddenCount;

  return {
    hiddenKinds,
    counts,
    hiddenCount,
    visibleCount,
    // Deliberately requires nodes to exist. A genuinely empty world with every type
    // switched off is still an empty world, and telling that writer their entities
    // are "hidden" would be a lie.
    allHidden: nodes.length > 0 && visibleCount === 0,
    // Keyed on a type being off rather than on anything being hidden, so the control
    // is honest even before the writer has created that kind of entity — otherwise
    // the filter turns itself invisible again the moment the world is small.
    filtering: hiddenKinds.length > 0,
  };
}

/**
 * "28 characters and 2 events", "1 location", "" when nothing is hidden.
 *
 * Counts, not type names: "characters are hidden" leaves the writer wondering whether
 * they had any. A number tells them what they are missing and that it still exists.
 */
export function describeHidden(summary: HiddenSummary): string {
  const parts = summary.hiddenKinds
    .filter(k => summary.counts[k] > 0)
    .map(k => {
      const n = summary.counts[k];
      const [one, many] = NOUNS[k];
      return `${n} ${n === 1 ? one : many}`;
    });

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  // No Oxford comma before "and" for two items; a serial comma for three.
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Title-case plural labels, matching the View drawer's own wording. */
const LABELS: Record<EntityKind, string> = {
  character: 'Characters',
  location: 'Locations',
  event: 'Events',
};

export const typeLabel = (kind: EntityKind): string => LABELS[kind];

/**
 * The sentence shown when someone creates an entity of a type they have hidden.
 *
 * This is the worst moment for a silent filter: they deliberately made something and
 * it did not appear. Saying nothing here is how a writer concludes that creation is
 * broken and stops trusting the page — the exact reading that cost an evening. (#754)
 *
 * Returns null when the type is visible, so the caller has no condition of its own.
 */
export function createdWhileHiddenMessage(
  name: string,
  kind: EntityKind,
  typeVisibility: TypeVisibility,
): string | null {
  if (typeVisibility[kind]) return null;
  // The name is echoed back so it is unmistakably THEIR thing that exists, not a
  // generic warning they might read as a failure to save.
  const subject = name.trim() || LABELS[kind].replace(/s$/, '');
  return `${subject} was added, but ${LABELS[kind]} are hidden, so it is not on the canvas.`;
}
